/**
 * ShareCierreActions.tsx — Compartir cierre como imagen / PDF / Gmail.
 *
 * QUÉ HACE:
 *  - Renderiza el botón "Compartir cierre" + dropdown con dos secciones,
 *    siguiendo el MISMO lenguaje visual que `ShareReceiptActions`:
 *
 *      COMO PDF / EMAIL · recomendado (visible por defecto)
 *        · Compartir PDF, Descargar PDF, Gmail
 *
 *      COMO IMAGEN (oculto — conservado; WhatsApp comprime fotos en cierres largos)
 *        · WhatsApp, Compartir imagen, Descargar PNG
 *
 *  - Tras pulsar una opción, muestra un PASO DE CONFIRMACIÓN:
 *       "¿Lograste compartir el cierre?" con [Sí, listo] / [Reintentar].
 *    Esto evita que un Web Share que resuelve sin envío real (sheet cerrado,
 *    popup bloqueado, etc.) libere por error un overlay anti-fraude.
 *
 * QUÉ EXPORTA: ShareCierreActions (named)
 *
 * QUÉ INVOCA:
 *  - useReceiptImage             (hook PNG)
 *  - useReceiptPdf               (hook PDF)
 *  - buildCierreShareCaption     (helper puro de caption)
 *  - buildWhatsappUrl            (helper puro de URL wa.me)
 *  - ShareOptionRow              (primitivo UI compartido)
 *
 * QUÉ NO HACE:
 *  - No comparte texto plano del cierre (deprecado: era editable).
 *  - No conoce de Firebase/mocks.
 *  - No depende del DOM más allá del `targetRef` recibido.
 *
 * REGLAS:
 *  - `onShared` SÓLO se invoca cuando el usuario confirma "Sí, listo".
 *  - Si una acción falla (cancelada, popup bloqueado), volvemos al estado
 *    inicial para que pueda reintentar.
 *  - El mensaje (caption) compartido es corto y NO contiene montos.
 */

import { RefObject, useEffect, useRef, useState } from "react";
import {
  Check,
  ChevronDown,
  ChevronUp,
  Download,
  FileDown,
  Image as ImageIcon,
  Loader2,
  Mail,
  MessageCircle,
  RotateCcw,
  SendHorizonal,
  Share2,
  X,
} from "lucide-react";
import { ShareOptionRow } from "@/components/ui/ShareOptionRow";
import { useReceiptImage } from "@/hooks/useReceiptImage";
import { useReceiptPdf } from "@/hooks/useReceiptPdf";
import {
  buildCierreFilename,
  buildCierreShareCaption,
  type CierreShareDocumentKind,
} from "../utils/buildCierreShareCaption";
import { buildWhatsappUrl, isValidWhatsappNumber } from "@/lib/utils/whatsapp";
import { copyTextToClipboard } from "@/lib/utils/clipboard";
import type { CierreCaja } from "../types/caja.types";
import {
  PDF_SHARE_ROW_DESCRIPTION,
  PDF_SHARE_SECTION_LABEL,
  SHOW_IMAGE_SHARE_OPTIONS,
} from "../constants/shareReceiptOptions";

type ShareChannel =
  | "whatsapp"
  | "image-share"
  | "image-download"
  | "pdf-share"
  | "pdf-download"
  | "gmail";

const CHANNEL_LABEL: Record<ShareChannel, string> = {
  whatsapp: "WhatsApp",
  "image-share": "Compartir imagen",
  "image-download": "Descargar PNG",
  "pdf-share": "Compartir PDF",
  "pdf-download": "Descargar PDF",
  gmail: "Gmail",
};

interface ShareCierreActionsProps {
  cierre: CierreCaja;
  /** Nombre del negocio (para el caption). */
  businessName?: string;
  /** Ref al ticket renderizado: se captura como PNG/PDF. */
  targetRef: RefObject<HTMLElement>;
  /** Teléfono opcional pre-cargado para WhatsApp. */
  defaultPhone?: string;
  /** Email opcional pre-cargado para Gmail. */
  defaultEmail?: string;
  className?: string;
  /**
   * Callback que SÓLO se invoca cuando el usuario confirma haber compartido
   * exitosamente. Útil para liberar overlays anti-fraude.
   */
  onShared?: (channel: ShareChannel) => void;
  /** Texto del botón trigger. Default: "Compartir cierre". */
  triggerLabel?: string;
  /** Texto del header del dropdown. Default: "Enviar cierre por". */
  menuHeader?: string;
  /** Tipo de documento para caption y nombre de archivo. */
  shareKind?: CierreShareDocumentKind;
  /**
   * Paso «¿Lograste compartir?» — solo cierre definitivo anti-fraude (`enforceShare`).
   * Reporte parcial e historial: compartir directo sin confirmación extra.
   */
  requireShareConfirmation?: boolean;
}

export const ShareCierreActions = ({
  cierre,
  businessName,
  targetRef,
  defaultPhone,
  defaultEmail,
  className = "",
  onShared,
  triggerLabel = "Compartir cierre",
  menuHeader = "Enviar cierre por",
  shareKind = "cierre",
  requireShareConfirmation = false,
}: ShareCierreActionsProps) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [showPhoneInput, setShowPhoneInput] = useState(false);
  const [phone, setPhone] = useState(defaultPhone ?? "");
  const [pendingChannel, setPendingChannel] = useState<ShareChannel | null>(
    null,
  );
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [showGmailHint, setShowGmailHint] = useState(false);
  const [showWhatsappHint, setShowWhatsappHint] = useState(false);
  const phoneInputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const filename = buildCierreFilename(cierre, shareKind);
  const caption = buildCierreShareCaption(cierre, businessName, shareKind);

  const {
    downloadImage,
    shareImage,
    isGenerating: isGenImg,
    canShareFiles: canShareImg,
  } = useReceiptImage({ filename });

  const {
    downloadPdf,
    sharePdf,
    isGenerating: isGenPdf,
    canShareFiles: canSharePdf,
  } = useReceiptPdf({ filename, format: "thermal" });

  const isGenerating = isGenImg || isGenPdf;

  useEffect(() => {
    if (!menuOpen) return;
    const onOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onOutside);
    return () => document.removeEventListener("mousedown", onOutside);
  }, [menuOpen]);

  const closeMenu = () => setMenuOpen(false);

  const resetState = () => {
    setPendingChannel(null);
    setErrorMsg(null);
    setShowPhoneInput(false);
  };

  const afterShareAttempt = (ok: boolean, channel: ShareChannel) => {
    if (!ok) return;
    if (requireShareConfirmation) {
      setPendingChannel(channel);
      return;
    }
    closeMenu();
    resetState();
  };

  // ── Acciones IMAGEN ──────────────────────────────────────────────────────
  /**
   * Fallback de WhatsApp en desktop (sin Web Share):
   *   1. Descarga el PNG (queda listo para adjuntar).
   *   2. Copia el caption al portapapeles (para pegar como pie de foto).
   *   3. Abre wa.me SIN texto pre-llenado (evita que el usuario presione
   *      Send y mande primero el texto, después la imagen como mensaje
   *      separado — lo que el usuario explícitamente NO quiere).
   *   4. Muestra hint instructivo: "Adjunta y pega el mensaje (Ctrl+V)".
   */
  const runDesktopWhatsappFallback = async (
    phoneToUse: string,
  ): Promise<boolean> => {
    const ok = await downloadImage(targetRef);
    if (!ok) {
      setErrorMsg("No se pudo preparar la imagen. Intenta descargarla.");
      return false;
    }
    await copyTextToClipboard(caption);
    const url = buildWhatsappUrl({ phone: phoneToUse, message: "" });
    if (url) window.open(url, "_blank", "noopener,noreferrer");
    setShowWhatsappHint(true);
    setTimeout(() => setShowWhatsappHint(false), 12000);
    return true;
  };

  const handleWhatsAppClick = async () => {
    setErrorMsg(null);
    if (canShareImg) {
      // Copiamos antes: si WhatsApp ignora el `text` del share sheet, el
      // usuario lo pega como pie de foto manual y todo llega JUNTO.
      await copyTextToClipboard(caption);
      setShowWhatsappHint(true);
      setTimeout(() => setShowWhatsappHint(false), 12000);
      const ok = await shareImage(targetRef, caption, caption);
      if (ok) afterShareAttempt(true, "whatsapp");
      else setErrorMsg("No se pudo compartir. Intenta otra opción.");
      return;
    }
    if (defaultPhone && isValidWhatsappNumber(defaultPhone)) {
      const ok = await runDesktopWhatsappFallback(defaultPhone);
      if (ok) afterShareAttempt(true, "whatsapp");
      return;
    }
    setShowPhoneInput(true);
    setTimeout(() => phoneInputRef.current?.focus(), 50);
  };

  const handlePhoneSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValidWhatsappNumber(phone)) return;
    const ok = await runDesktopWhatsappFallback(phone);
    setShowPhoneInput(false);
    if (ok) afterShareAttempt(true, "whatsapp");
  };

  const handleShareImage = async () => {
    setErrorMsg(null);
    await copyTextToClipboard(caption);
    setShowWhatsappHint(true);
    setTimeout(() => setShowWhatsappHint(false), 12000);
    const ok = await shareImage(targetRef, caption, caption);
    if (ok) afterShareAttempt(true, "image-share");
    else setErrorMsg("No se pudo compartir. Intenta otra opción.");
  };

  const handleDownloadImage = async () => {
    setErrorMsg(null);
    const ok = await downloadImage(targetRef);
    if (ok) afterShareAttempt(true, "image-download");
    else setErrorMsg("No se pudo descargar la imagen. Intenta de nuevo.");
  };

  // ── Acciones PDF / EMAIL ────────────────────────────────────────────────
  const handleSharePdf = async () => {
    setErrorMsg(null);
    await copyTextToClipboard(caption);
    setShowWhatsappHint(true);
    setTimeout(() => setShowWhatsappHint(false), 12000);
    const ok = await sharePdf(targetRef, caption, caption);
    if (ok) afterShareAttempt(true, "pdf-share");
    else setErrorMsg("No se pudo compartir el PDF. Intenta otra opción.");
  };

  const handleDownloadPdf = async () => {
    setErrorMsg(null);
    const ok = await downloadPdf(targetRef);
    if (ok) afterShareAttempt(true, "pdf-download");
    else setErrorMsg("No se pudo descargar el PDF. Intenta de nuevo.");
  };

  const handleGmail = async () => {
    setErrorMsg(null);
    const downloaded = await downloadPdf(targetRef);
    if (!downloaded) {
      setErrorMsg("No se pudo preparar el PDF para Gmail. Intenta de nuevo.");
      return;
    }
    setShowGmailHint(true);
    setTimeout(() => setShowGmailHint(false), 8000);
    const subject = caption;
    const docLabel =
      shareKind === "parcial"
        ? "reporte parcial de caja"
        : shareKind === "jornada"
          ? "resumen global de jornada"
          : "cierre de caja";
    const body = `${caption}\n\nAdjunto el reporte oficial del ${docLabel} en PDF (no editable).`;
    const params = new URLSearchParams();
    if (defaultEmail) params.set("to", defaultEmail);
    params.set("su", subject);
    params.set("body", body);
    const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&${params.toString()}`;
    window.open(gmailUrl, "_blank", "noopener,noreferrer");
    afterShareAttempt(true, "gmail");
  };

  const handleConfirmShared = () => {
    if (!pendingChannel) return;
    onShared?.(pendingChannel);
    resetState();
    closeMenu();
  };

  // ── Render del cuerpo del dropdown ──────────────────────────────────────
  const renderPanelBody = () => {
    if (requireShareConfirmation && pendingChannel) {
      return (
        <div className="p-3 flex flex-col gap-2 bg-emerald-50 dark:bg-emerald-900/20">
          <p className="text-[12px] font-bold text-emerald-800 dark:text-emerald-200 leading-snug">
            ¿Lograste compartir el cierre por{" "}
            <span className="underline">{CHANNEL_LABEL[pendingChannel]}</span>?
          </p>
          <p className="text-[10px] text-emerald-700 dark:text-emerald-300 leading-snug">
            Si el envío fue exitoso, presiona <strong>Sí, listo</strong>. Si no,
            presiona <strong>Reintentar</strong>.
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleConfirmShared}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg text-sm font-bold text-white bg-emerald-600 hover:bg-emerald-700 transition-colors shadow"
            >
              <Check size={14} />
              Sí, listo
            </button>
            <button
              type="button"
              onClick={resetState}
              className="flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg text-sm font-bold text-emerald-700 dark:text-emerald-200 bg-white dark:bg-slate-700 border border-emerald-200 dark:border-slate-600 hover:bg-emerald-50 dark:hover:bg-slate-600 transition-colors"
            >
              <RotateCcw size={14} />
              Reintentar
            </button>
          </div>
        </div>
      );
    }

    return (
      <>
        {/* Sección IMAGEN — oculta en UI; lógica conservada */}
        {SHOW_IMAGE_SHARE_OPTIONS && (
          <>
            <div className="px-4 pt-2 pb-1 flex items-center gap-1.5">
              <ImageIcon
                size={11}
                className="text-gray-400 dark:text-slate-500"
              />
              <span className="text-[10px] font-bold text-gray-400 dark:text-slate-500 uppercase tracking-wider">
                Como imagen · recomendado
              </span>
            </div>
            <div className="px-1.5 pb-1.5 flex flex-col gap-1">
              <ShareOptionRow
                palette="whatsapp"
                icon={<MessageCircle size={14} />}
                label="WhatsApp"
                description="Imagen + mensaje breve"
                onClick={() => void handleWhatsAppClick()}
                disabled={isGenerating}
              />
              {canShareImg && (
                <ShareOptionRow
                  palette="image"
                  icon={<Share2 size={14} />}
                  label="Compartir imagen"
                  description="Enviar a cualquier app instalada"
                  onClick={() => void handleShareImage()}
                  disabled={isGenerating}
                />
              )}
              <ShareOptionRow
                palette="download"
                icon={<Download size={14} />}
                label="Descargar PNG"
                description="Guardar el ticket como imagen"
                onClick={() => void handleDownloadImage()}
                disabled={isGenerating}
              />
            </div>
          </>
        )}

        {/* Sección PDF / EMAIL */}
        <div
          className={`px-4 pt-2 pb-1 flex items-center gap-1.5${
            SHOW_IMAGE_SHARE_OPTIONS
              ? " border-t border-gray-100 dark:border-slate-700"
              : ""
          }`}
        >
          <FileDown size={11} className="text-gray-400 dark:text-slate-500" />
          <span className="text-[10px] font-bold text-gray-400 dark:text-slate-500 uppercase tracking-wider">
            {PDF_SHARE_SECTION_LABEL}
          </span>
        </div>
        <div className="px-1.5 pb-1.5 flex flex-col gap-1">
          {canSharePdf && (
            <ShareOptionRow
              palette="share"
              icon={<Share2 size={14} />}
              label="Compartir PDF"
              description={PDF_SHARE_ROW_DESCRIPTION}
              onClick={() => void handleSharePdf()}
              disabled={isGenerating}
            />
          )}
          <ShareOptionRow
            palette="download"
            icon={<FileDown size={14} />}
            label="Descargar PDF"
            description="Guardar el ticket en este dispositivo"
            onClick={() => void handleDownloadPdf()}
            disabled={isGenerating}
          />
          <ShareOptionRow
            palette="email"
            icon={<Mail size={14} />}
            label="Gmail"
            description="Descarga el PDF y abre Gmail listo para enviar"
            onClick={() => void handleGmail()}
            disabled={isGenerating}
          />
        </div>

        <p className="px-4 py-2 text-[10px] text-gray-500 dark:text-slate-400 leading-snug border-t border-gray-100 dark:border-slate-700 bg-gray-50/60 dark:bg-slate-700/40">
          Mensaje que se enviará: <em>{caption}</em>
        </p>

        {showGmailHint && (
          <p className="mx-1.5 mb-1.5 text-[11px] text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 rounded-lg px-3 py-2 leading-snug">
            PDF descargado. En Gmail, haz clic en el clip (adjuntar) y
            selecciona el archivo descargado.
          </p>
        )}

        {showWhatsappHint && (
          <p className="mx-1.5 mb-1.5 text-[11px] text-emerald-800 dark:text-emerald-200 bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-700 rounded-lg px-3 py-2 leading-snug">
            Mensaje <strong>copiado al portapapeles</strong>. Si la app destino
            no lo añade automáticamente,{" "}
            <strong>pégalo (Ctrl+V / mantener presionado)</strong> como pie de
            foto al adjuntar el archivo. Así llega <strong>todo junto</strong>.
          </p>
        )}

        {errorMsg && (
          <p className="mx-1.5 mb-1.5 text-[11px] text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-md px-3 py-2 flex items-center gap-2">
            <X size={12} className="shrink-0" />
            {errorMsg}
          </p>
        )}

        {showPhoneInput && (
          <form
            onSubmit={(e) => void handlePhoneSubmit(e)}
            className="mx-1.5 mb-1.5 flex items-center gap-1.5 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-1.5"
          >
            <input
              ref={phoneInputRef}
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="Teléfono del destinatario"
              className="flex-1 min-w-0 px-2 py-1 text-[11px] bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded outline-none focus:border-green-500 dark:text-slate-100"
            />
            <button
              type="submit"
              disabled={!isValidWhatsappNumber(phone)}
              className="px-2.5 py-1 text-[11px] font-bold rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Abrir
            </button>
            <button
              type="button"
              onClick={() => setShowPhoneInput(false)}
              className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-slate-200 transition-colors"
              title="Cancelar"
            >
              <X size={12} />
            </button>
          </form>
        )}
      </>
    );
  };

  return (
    <div className={`flex flex-col gap-2 ${className}`}>
      <div className="relative" ref={menuRef}>
        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          disabled={isGenerating}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-bold text-sm text-white bg-primary-600 hover:bg-primary-700 active:bg-primary-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-md shadow-primary-500/20"
        >
          {isGenerating ? (
            <Loader2 size={16} className="animate-spin" />
          ) : (
            <SendHorizonal size={16} />
          )}
          {isGenerating ? "Generando…" : triggerLabel}
          {!isGenerating &&
            (menuOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
        </button>

        {menuOpen && (
          <div className="absolute bottom-full left-0 right-0 mb-2 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl shadow-xl overflow-hidden z-50">
            <div className="px-4 py-2.5 border-b border-gray-100 dark:border-slate-700 flex items-center justify-between">
              <span className="text-[11px] font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wide">
                {menuHeader}
              </span>
              <button
                type="button"
                onClick={closeMenu}
                className="p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-slate-200 transition-colors rounded"
              >
                <X size={13} />
              </button>
            </div>
            {renderPanelBody()}
          </div>
        )}
      </div>
    </div>
  );
};
